import { routePaths } from "../../../app/navigation";
import type { ClockState } from "../../../domain/clock";
import type { WorkDay } from "../../../domain/workday";
import { hasAccessRight } from "../../../shared/auth/accessRights";

/** Who is signed in and where, as far as the compact clock needs it. */
export interface CompactClockAudience {
  /** Roles from `GET /me` or the last known roles, `undefined` while unknown. */
  readonly roles: readonly string[] | undefined;
  /** Whether the person has an employment, `undefined` while unknown. */
  readonly hasEmployment: boolean | undefined;
  /** Path of the current page. */
  readonly pathname: string;
}

/**
 * Returns whether the app bar shows the compact clock (F-75): only for people who record their own
 * working time (`AccessOwnRecords`) and have an employment, never while the roles are unknown, and
 * not on the start page „Heute“, which shows the full clock. An employment that is not known yet
 * (a profile stored by an earlier app version, offline) does not hide it, like the start page,
 * which shows the clock then too. Operator staff and shared terminals use layouts of their own
 * without the clock.
 *
 * @param audience - Roles, employment and page.
 * @returns `true` if the clock is shown.
 */
export function showsCompactClock(audience: CompactClockAudience): boolean {
  const recordsOwnTime =
    audience.roles !== undefined && hasAccessRight(audience.roles, "AccessOwnRecords");
  const employed = audience.hasEmployment !== false;
  const onStartPage = audience.pathname === routePaths.clock;
  const shown = recordsOwnTime && employed && !onStartPage;

  return shown;
}

/**
 * Returns the working time the compact clock shows next to the state: that of the current working
 * day (also a closed one of today, F-17), or `null` without a working day.
 *
 * @param state - The clock state.
 * @param day - The current working day, or `null`.
 * @returns The duration in seconds, or `null`.
 */
export function compactClockSeconds(state: ClockState, day: WorkDay | null): number | null {
  const seconds =
    day === null || (state === "Off" && day.workSeconds === 0) ? null : day.workSeconds;

  return seconds;
}
